import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Avatar,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Button,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import LaunchIcon from '@material-ui/icons/Launch';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import { format, parseISO, isSameDay } from 'date-fns';
import { es } from 'date-fns/locale';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import api from '../../services/api';
import { i18n } from "../../translate/i18n";
import formatToCurrency from "../../utils/formatToCurrency";

const useStyles = makeStyles(theme => ({
  card: {
    borderRadius: 10,
    marginBottom: 8,
    cursor: 'grab',
    overflow: 'hidden',
    background: theme.palette.type === 'dark' ? '#1e293b' : '#ffffff',
    border: '1px solid',
    borderColor: theme.palette.type === 'dark' ? 'rgba(91,45,142,0.12)' : 'rgba(0,0,0,0.07)',
    boxShadow: theme.palette.type === 'dark'
      ? '0 2px 8px rgba(0,0,0,0.3)'
      : '0 1px 4px rgba(0,0,0,0.07)',
    transition: 'box-shadow 0.2s, transform 0.15s',
    '&:hover': {
      boxShadow: '0 6px 18px rgba(91,45,142,0.15)',
      transform: 'translateY(-1px)',
    },
  },
  cardDragging: {
    boxShadow: '0 12px 32px rgba(91,45,142,0.30)',
    transform: 'rotate(1.5deg)',
    opacity: 0.95,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 10px 7px',
  },
  avatar: {
    width: 28,
    height: 28,
    fontSize: 12,
    flexShrink: 0,
  },
  contactName: {
    fontSize: 13,
    fontWeight: 700,
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: theme.palette.type === 'dark' ? '#e2e8f0' : '#1e293b',
  },
  ticketId: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.palette.type === 'dark' ? '#475569' : '#94a3b8',
    flexShrink: 0,
  },
  unreadBadge: {
    background: '#22c55e',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    padding: '1px 5px',
    borderRadius: 10,
    lineHeight: '16px',
    minWidth: 16,
    textAlign: 'center',
    flexShrink: 0,
  },
  divider: {
    height: 1,
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
    margin: '0 10px',
  },
  messageRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 10px',
    gap: 8,
  },
  lastMessage: {
    fontSize: 12,
    color: theme.palette.type === 'dark' ? '#64748b' : '#94a3b8',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    flex: 1,
  },
  messageTime: {
    fontSize: 10,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  messageTimeNormal: {
    color: theme.palette.type === 'dark' ? '#475569' : '#94a3b8',
  },
  messageTimeUnread: {
    color: '#22c55e',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 8px 8px',
    gap: 6,
  },
  valueBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: 6,
    transition: 'background 0.15s',
    '&:hover': {
      background: theme.palette.type === 'dark' ? 'rgba(91,45,142,0.10)' : 'rgba(91,45,142,0.06)',
    },
  },
  valueText: {
    fontSize: 12,
    fontWeight: 700,
    color: '#5B2D8E',
  },
  noValueText: {
    fontSize: 11,
    color: theme.palette.type === 'dark' ? '#475569' : '#94a3b8',
  },
  rightFooter: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  agentChip: {
    fontSize: 9,
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? '#94a3b8' : '#64748b',
    background: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)',
    padding: '2px 6px',
    borderRadius: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.03em',
  },
  viewBtn: {
    width: 26,
    height: 26,
    padding: 0,
    color: '#5B2D8E',
    background: 'rgba(91,45,142,0.08)',
    borderRadius: 7,
    '&:hover': {
      background: 'rgba(91,45,142,0.18)',
    },
  },
}));

const KanbanCard = ({ ticket, index, updateTicket }) => {
  const classes = useStyles();
  const history = useHistory();

  const [open, setOpen] = useState(false);
  const [newValue, setNewValue] = useState('');

  const customFields = ticket.contact.extraInfo || [];
  const valueFieldIndex = customFields.findIndex(field => field.name === 'valor');
  const valueField = valueFieldIndex !== -1 ? customFields[valueFieldIndex] : null;
  const opportunityValue = valueField ? parseFloat(valueField.value) : null;
  const unreadCount = Number(ticket.unreadMessages);

  const handleCardClick = (e) => {
    e.stopPropagation();
    history.push(`/tickets/${ticket.uuid}`);
  };

  const handleOpenModal = (e) => {
    e.stopPropagation();
    setNewValue(valueField ? valueField.value.toString() : '');
    setOpen(true);
  };

  const handleCloseModal = () => setOpen(false);

  const updateContactValue = async (contactId, value) => {
    try {
      await api.put(`/contacts/${contactId}`, {
        extraInfo: [{ name: 'valor', value: value.toString() }],
      });
    } catch (error) {
      console.error('Error al actualizar el valor:', error);
    }
  };

  const removeContactValue = async (e) => {
    e.stopPropagation();
    try {
      await api.put(`/contacts/${ticket.contact.id}`, { extraInfo: [] });
      if (valueFieldIndex !== -1) customFields.splice(valueFieldIndex, 1);
      updateTicket({ ...ticket });
    } catch (error) {
      console.error('Error al eliminar el valor:', error);
    }
  };

  const handleSaveValue = async () => {
    await updateContactValue(ticket.contact.id, newValue);
    if (valueField) {
      valueField.value = newValue;
    } else {
      if (!ticket.contact.extraInfo) ticket.contact.extraInfo = [];
      ticket.contact.extraInfo.push({ name: 'valor', value: newValue });
    }
    updateTicket({ ...ticket });
    setOpen(false);
  };

  const timeStr = isSameDay(parseISO(ticket.updatedAt), new Date())
    ? format(parseISO(ticket.updatedAt), 'HH:mm', { locale: es })
    : format(parseISO(ticket.updatedAt), 'dd/MM', { locale: es });

  return (
    <Draggable draggableId={ticket.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          className={`${classes.card} ${snapshot.isDragging ? classes.cardDragging : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {/* Header */}
          <div className={classes.header}>
            <Avatar src={ticket.contact.urlPicture} className={classes.avatar}>
              {ticket.contact.name?.[0]}
            </Avatar>
            <Tooltip title={ticket.contact.name || ''}>
              <Typography className={classes.contactName}>
                {ticket.contact.name?.substring(0, 20) || '—'}
              </Typography>
            </Tooltip>
            {unreadCount > 0 && (
              <span className={classes.unreadBadge}>{unreadCount}</span>
            )}
            <span className={classes.ticketId}>#{ticket.id}</span>
          </div>

          <div className={classes.divider} />

          {/* Last message + time */}
          <div className={classes.messageRow}>
            <Tooltip title={ticket.lastMessage || ''}>
              <Typography className={classes.lastMessage}>
                {ticket.lastMessage?.substring(0, 35) || '—'}
              </Typography>
            </Tooltip>
            <Typography className={`${classes.messageTime} ${unreadCount > 0 ? classes.messageTimeUnread : classes.messageTimeNormal}`}>
              {timeStr}
            </Typography>
          </div>

          <div className={classes.divider} />

          {/* Footer: value + agent + action */}
          <div className={classes.footer}>
            {/* Value */}
            <div className={classes.valueBtn} onClick={handleOpenModal}>
              <AttachMoneyIcon style={{ fontSize: 13, color: opportunityValue ? '#5B2D8E' : '#94a3b8' }} />
              {opportunityValue !== null ? (
                <>
                  <span className={classes.valueText}>{formatToCurrency(opportunityValue)}</span>
                  <IconButton
                    size="small"
                    onClick={removeContactValue}
                    style={{ padding: 1, color: '#ef4444' }}
                  >
                    <CloseIcon style={{ fontSize: 10 }} />
                  </IconButton>
                </>
              ) : (
                <span className={classes.noValueText}>{i18n.t('kanbanCard.assignValue')}</span>
              )}
            </div>

            {/* Agent + View */}
            <div className={classes.rightFooter}>
              {ticket.user && (
                <Tooltip title={ticket.user.name}>
                  <span className={classes.agentChip}>
                    {ticket.user.name.substring(0, 8)}
                  </span>
                </Tooltip>
              )}
              <Tooltip title={i18n.t('kanbanCard.viewTicket')}>
                <IconButton className={classes.viewBtn} size="small" onClick={handleCardClick}>
                  <LaunchIcon style={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Value Dialog */}
          <Dialog open={open} onClose={handleCloseModal} PaperProps={{ style: { borderRadius: 12 } }}>
            <DialogTitle>
              {valueField ? i18n.t('kanbanCard.editValue') : i18n.t('kanbanCard.assignOpportunityValue')}
            </DialogTitle>
            <DialogContent>
              <TextField
                label={i18n.t('kanbanCard.opportunityValue')}
                type="number"
                fullWidth
                value={newValue}
                onChange={(e) => setNewValue(e.target.value)}
                variant="outlined"
                size="small"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseModal} color="secondary" variant="outlined">
                {i18n.t('kanbanCard.cancel')}
              </Button>
              <Button onClick={handleSaveValue} color="primary" variant="contained">
                {i18n.t('kanbanCard.save')}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </Draggable>
  );
};

export default KanbanCard;
